import studentApi from './studentApi';
import { API_ENDPOINTS } from './api';
import type { TopicRecommendation } from './recommendationService';

// Types matching backend DTOs
export interface StudentDashboardDTO {
  courses: CourseDashboardDTO[];
}

export interface CourseDashboardDTO {
  courseId: number;
  courseName: string;
  courseDescription: string;
  courseType?: string;
  currentTopic: CurrentTopicDTO | null;
  classProgress: ClassProgressDTO | null;
  pendingItems: PendingItemsDTO;
  recentAchievements: AchievementDTO[];
  completedTopics: CompletedTopicDTO[];
  requizMasterPoints: number;
  requizMasterRanking: RequizMasterRankingDTO | null;
}

export interface CurrentTopicDTO {
  topicId: number;
  title: string;
  subjectName: string;
  subjectId: number;
  progress: number;
  status: 'not_started' | 'in_progress' | 'completed';
  estimatedTime: string;
}

export interface ClassProgressDTO {
  currentTopicTitle: string;
  studentsBehind: number;
  totalStudents: number;
  weeksBehind: number;
}

export interface PendingItemsDTO {
  assignments: number;
  quizzes: number;
  overdueAssignments: number;
}

export interface AchievementDTO {
  id: number;
  title: string;
  type: 'quiz' | 'assignment' | 'topic' | 'award';
  score?: number;
  earnedDate: string;
}

export interface CompletedTopicDTO {
  topicId: number;
  title: string;
  subjectName: string;
  subjectId: number;
  completedAt: string;
  quizScore?: number;
  estimatedTime: string;
  requizCompleted: boolean;
}

export interface RequizMasterRankingDTO {
  position: number;
  totalStudents: number;
  totalPoints: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * Get student dashboard data (legacy endpoint — kept for backward compatibility).
 */
export const getStudentDashboard = async (): Promise<StudentDashboardDTO> => {
  try {
    const response = await studentApi.get<ApiResponse<StudentDashboardDTO>>(
      API_ENDPOINTS.STUDENT_DASHBOARD.DASHBOARD
    );
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching student dashboard:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch dashboard');
  }
};

/** Card fields for the home page. The courses page already returns this shape's source. */
export function mapCourseDashboardToSummary(course: CourseDashboardDTO): CourseSummaryDTO {
  const topic = course.currentTopic;
  const progressPercent = topic?.progress ?? 0;
  const statusLabel =
    topic?.status === 'completed' ? 'Completed'
    : topic?.status === 'in_progress' || progressPercent > 0 ? 'Active'
    : 'Not Started';
  return {
    courseId: course.courseId,
    courseName: course.courseName,
    progressPercent,
    statusLabel,
    currentTopic: topic
      ? {
          topicId: topic.topicId,
          topicName: topic.title,
          resumeReason: topic.status === 'in_progress' ? 'IN_PROGRESS' : 'NOT_STARTED',
        }
      : null,
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Aggregated dashboard types & service (new single-call endpoint)
// ──────────────────────────────────────────────────────────────────────────────

export interface StudentInfoDTO {
  id: number;
  name: string;
  email: string;
}

export interface GamificationSummaryDTO {
  rankName: string;
  rankLevel: number;
  currentPoints: number;
  pointsToNextRank: number | null;
  percentToNextRank: number;
  currentStreakDays: number;
  globalRank: number;
  weeklyGoalPercent: number;
}

export interface ResumeTopicDTO {
  topicId: number;
  topicName: string;
  /** "IN_PROGRESS" | "NOT_STARTED" | "RECOMMENDED" */
  resumeReason: string;
}

export interface CourseSummaryDTO {
  courseId: number;
  courseName: string;
  progressPercent: number;
  currentTopic: ResumeTopicDTO | null;
  statusLabel: string;
}

export interface RecentActivityItemDTO {
  eventType: string;
  description: string;
  points: number | null;
  occurredAt: string;
}

export interface DashboardStatsDTO {
  totalTopicsCompleted: number;
  totalQuizzesTaken: number;
  quizPassRate: number;
  weeklyActivityCount: number;
}

export interface AggregatedDashboardDTO {
  student: StudentInfoDTO;
  gamification: GamificationSummaryDTO;
  courses: CourseSummaryDTO[];
  topRecommendations: TopicRecommendation[];
  recentActivity: RecentActivityItemDTO[];
  stats: DashboardStatsDTO;
}

/**
 * Fetches the full aggregated student dashboard from the new single-call endpoint.
 * Falls back gracefully to null so the UI can show partial/mock data.
 */
export const getAggregatedDashboard = async (): Promise<AggregatedDashboardDTO> => {
  const response = await studentApi.get<ApiResponse<AggregatedDashboardDTO>>(
    API_ENDPOINTS.STUDENT_DASHBOARD.AGGREGATED_DASHBOARD
  );
  return response.data.data;
};



